import { IconButton } from '@components/core/icon-button.component';
import { cn } from '@lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React from 'react';

// =============================================================================
// TYPES
// =============================================================================

export type ModalSidebarProps = {
  className?: string;
  children: React.ReactNode;
  isOpen: boolean;
  header?: React.ReactNode;
  setIsOpen: (isOpen: boolean) => void;
  sidebarContent: React.ReactNode;
  sidebarWidth?: string;
};

// =============================================================================
// COMPONENTS
// =============================================================================

/**
 * Custom collapsible sidebar component for use within modals
 *
 * This component provides a simple collapsible sidebar that works properly
 * within modal containers without the positioning issues of Shadcn Sidebar.
 */
export const ModalSidebar: React.FC<ModalSidebarProps> = (props) => {
  const { children, className, isOpen, setIsOpen, header, sidebarContent, sidebarWidth = 'w-80' } = props;

  return (
    <div className="flex h-full w-full relative">
      {/* Sidebar */}
      <div
        className={cn(
          'flex flex-col border-r border-gray-200 bg-white transition-all duration-300 ease-in-out overflow-hidden rounded-bl-xl',
          isOpen ? sidebarWidth : 'w-0',
          className,
        )}
      >
        {/* Sidebar content wrapper to prevent content from being squished */}
        <div className={cn('flex flex-col h-full', sidebarWidth)}>
          {header && <div className="border-b border-gray-200 p-4 shrink-0">{header}</div>}
          <div className="flex-1 overflow-y-auto p-4">{sidebarContent}</div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col relative overflow-hidden">
        {/* Toggle button */}
        <div className="absolute top-4 left-4 z-10">
          <IconButton
            ariaLabel={isOpen ? 'Collapse' : 'Expand'}
            icon={isOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
            onClick={() => setIsOpen(!isOpen)}
            size="md"
            title={isOpen ? 'Collapse' : 'Expand'}
            variant="secondary"
          />
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-hidden">{children}</div>
      </div>
    </div>
  );
};
